import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getCustom, guessSyllables, known } from "@/lib/poetry/dict";
import { docLines } from "@/lib/poetry/lines";
import { saveProns } from "@/components/poetry/PoetryWidget";

const unknownWords = (editor) => {
  if (!editor) return [];
  const set = new Set();
  docLines(editor.state.doc).forEach((l) => (l.header ? [] : l.text.match(/[A-Za-z’'][A-Za-z’']*/g) || []).forEach((w) => { const k = w.toLowerCase().replace(/[’']/g, "'").replace(/^'+|'+$/g, ""); if (k && !known(k)) set.add(k); }));
  return [...set].slice(0, 40);
};

export const WordsTab = ({ editor }) => {
  const list = getCustom();
  const [form, setForm] = useState({ word: "", syllables: "", rhymes_like: "" });
  const save = async (next) => { try { await saveProns(next); } catch { toast.error("Saved on this device; will sync when you're online"); } };
  const add = async () => {
    const word = form.word.trim().toLowerCase();
    const n = parseInt(form.syllables, 10);
    if (!word || !(n >= 1 && n <= 12)) return toast.error("Enter a word and 1–12 syllables");
    await save([...list.filter((p) => p.word !== word), { word, syllables: n, rhymes_like: form.rhymes_like.trim().toLowerCase() }]);
    setForm({ word: "", syllables: "", rhymes_like: "" });
    toast.success(`Saved “${word}”`);
  };
  const unknown = unknownWords(editor).filter((w) => !list.some((p) => p.word === w));
  return (
    <div className="space-y-3" data-testid="poetry-words">
      <p className="text-xs leading-relaxed text-[#A8A090]">Teach LoreRight how your names and invented words sound. Give the syllable count and, optionally, a real word it rhymes with.</p>
      <div className="grid grid-cols-[1fr_56px] gap-1.5">
        <Input value={form.word} onChange={(e) => setForm({ ...form, word: e.target.value })} placeholder="Word, e.g. Aelindra" maxLength={40} className="h-8 text-sm" data-testid="pron-word" />
        <Input value={form.syllables} onChange={(e) => setForm({ ...form, syllables: e.target.value })} placeholder="Syl." inputMode="numeric" className="h-8 text-sm" data-testid="pron-syllables" />
        <Input value={form.rhymes_like} onChange={(e) => setForm({ ...form, rhymes_like: e.target.value })} placeholder="Rhymes like, e.g. Sandra" maxLength={40} className="col-span-2 h-8 text-sm" data-testid="pron-rhymes-like" />
      </div>
      <Button size="sm" onClick={add} data-testid="pron-add"><Plus className="mr-1 h-3.5 w-3.5" />Save word</Button>
      {unknown.length > 0 && (
        <div className="space-y-1" data-testid="pron-unknown"><div className="lf-label">Estimated in this chapter</div>
          <div className="flex flex-wrap gap-1">{unknown.map((w) => <button key={w} onClick={() => setForm({ word: w, syllables: String(guessSyllables(w)), rhymes_like: "" })} title="Click to set how it sounds" className="rounded-full border border-dashed border-[#2D3442] px-2 py-0.5 text-xs text-[#D6CCBC] hover:border-[#D97706]" data-testid={`pron-unknown-${w}`}>{w} <span className="text-[#7A7468]">~{guessSyllables(w)}</span></button>)}</div></div>
      )}
      {list.length > 0 && (
        <ul className="max-h-48 space-y-1 overflow-y-auto pr-1" data-testid="pron-list">
          {list.map((p) => (
            <li key={p.word} className="flex items-center gap-2 text-xs text-[#E8DFD1]" data-testid={`pron-item-${p.word}`}>
              <span className="flex-1 truncate"><b>{p.word}</b> · {p.syllables} syl{p.rhymes_like && <span className="text-[#A8A090]"> · rhymes like {p.rhymes_like}</span>}</span>
              <button onClick={() => save(list.filter((x) => x.word !== p.word))} title="Remove" className="text-[#7A7468] hover:text-[#F87171]" data-testid={`pron-remove-${p.word}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
